import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { authenticatedRequest } from "../../../config/axios";
import {
  glassDialogPaperProps,
  glassDialogBackdropProps,
} from "./consultationsGlassDialog";

function MeetingLogEntryIcon({ entry }) {
  if (
    entry?.yarpaServiceType === "mirshamHeader" ||
    entry?.yarpaServiceType === "mirshamimLines"
  ) {
    return <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  const a = (entry?.action || "").toLowerCase();
  if (a.includes("created")) {
    return <AddCircleOutlineIcon sx={{ fontSize: 20 }} />;
  }
  if (a.includes("status")) {
    return <FlagOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  if (a.includes("scheduled")) {
    return <EventOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  if (a.includes("updated")) {
    return <EditOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  return <HistoryOutlinedIcon sx={{ fontSize: 20 }} />;
}

/**
 * Meeting log dialog used only on the Consultations route (AdminConsultations).
 * Other pages continue to use ./MeetingLog.jsx.
 */
export default function ConsultationsMeetingLogModal({
  open,
  onClose,
  meetingId,
  lang = "EN",
}) {
  const { t } = useTranslation();
  const dir = lang === "HE" ? "rtl" : "ltr";
  const [meetinglogData, setMeetingLogData] = useState([]);
  const [loading, setLoading] = useState(true);

  const meetingLogDetails = useCallback(async () => {
    if (!meetingId) return;
    try {
      setLoading(true);
      const response = await authenticatedRequest({
        method: "get",
        url: `/admin/getMeetingLogs/${meetingId}`,
        headers: {
          "Content-Type": "application/json",
        },
      });
      if ([200, 201, 202].includes(response.status)) {
        setMeetingLogData(response.data.meetingLogs);
      }
    } catch {
      /* network / server error */
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    if (open && meetingId) {
      meetingLogDetails();
    }
  }, [open, meetingId, meetingLogDetails]);

  const iconWrapSx = {
    width: 36,
    height: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    bgcolor: "rgba(241, 245, 249, 0.95)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    color: "rgba(51, 65, 85, 0.88)",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dir={dir}
      maxWidth="md"
      fullWidth
      aria-labelledby="consultations-meeting-log-title"
      PaperProps={glassDialogPaperProps()}
      BackdropProps={glassDialogBackdropProps()}
    >
      <DialogTitle
        id="consultations-meeting-log-title"
        sx={{
          fontWeight: 700,
          fontSize: "1.125rem",
          pb: 1,
          pt: 2.5,
          px: 3,
          color: "rgba(49, 46, 129, 0.95)",
          textAlign: "center",
        }}
      >
        Meeting log
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pt: 0,
          pb: 2,
          maxHeight: "min(62vh, 520px)",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={6}
          >
            <CircularProgress size={36} sx={{ color: "rgba(67, 56, 202, 0.65)" }} />
          </Box>
        ) : meetinglogData.length !== 0 ? (
          <Stack spacing={1.25}>
            {meetinglogData.map((data, i) => {
              const formattedDate = `${moment(data.updatedAt).format(
                "DD.MM.YYYY"
              )} ${moment(data.updatedAt).format("h:mm:ss a")}`;

              let line = null;

              if (
                data?.responseBody?.StatusResponse?.StatusCode === 0 ||
                data?.responseBody?.statusResponse?.errorCodeSeverity === "S"
              ) {
                if (data?.yarpaServiceType === "mirshamHeader") {
                  line = (
                    <>
                      Prescription header number {data?.mirshamId} has
                      successfully been uploaded to Yarpa ({formattedDate})
                    </>
                  );
                } else if (data?.yarpaServiceType === "mirshamimLines") {
                  line = (
                    <>
                      Prescription lines for header number{" "}
                      {data?.requestBody?.MirshamId
                        ? data?.requestBody?.MirshamId
                        : data?.requestBody?.mirshamId}{" "}
                      has successfully been uploaded to Yarpa ({formattedDate})
                    </>
                  );
                }
              }

              if (!line) {
                line = (
                  <>
                    {data.action} ({formattedDate})
                  </>
                );
              }

              return (
                <Stack
                  key={i}
                  direction="row"
                  alignItems="flex-start"
                  spacing={1.5}
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    border: "1px solid rgba(226, 232, 240, 0.95)",
                    bgcolor: "rgba(248, 250, 252, 0.65)",
                  }}
                >
                  <Box sx={iconWrapSx}>
                    <MeetingLogEntryIcon entry={data} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      textAlign: "start",
                      lineHeight: 1.55,
                      color: "rgba(30, 41, 59, 0.92)",
                      fontSize: "0.875rem",
                      pt: 0.25,
                    }}
                  >
                    {line}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              py: 4,
              color: "rgba(100, 116, 139, 0.95)",
            }}
          >
            {t("This meeting has no log")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 0,
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            minWidth: 140,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            py: 1,
            px: 3,
            boxShadow: "0 8px 20px rgba(67, 56, 202, 0.28)",
            bgcolor: "rgba(67, 56, 202, 0.95)",
            "&:hover": {
              bgcolor: "rgba(55, 48, 163, 0.98)",
              boxShadow: "0 10px 24px rgba(67, 56, 202, 0.35)",
            },
          }}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
